import { DifficultyLevel } from '../interfaces/question.interface';
import { PizzaRound } from '../interfaces/cutting-pizza.interface';

/**
 * Curated add/subtract problems for Cutting Pizza (Grade 4: like
 * denominators, all within one pizza so 0 <= answer <= den). A mix of
 * plain sums/differences and "makes a whole pizza" answers (== den) so
 * difficulty ramps. Denominators are classic pizza cuts: 2,3,4,6,8.
 */
interface Spec { a: number; op: '+' | '-'; b: number; d: number; }

const POOL: Spec[] = [
  // ── Easy: small pizzas (halves, thirds, quarters) ──
  { a: 1, op: '+', b: 1, d: 3 }, // 1/3 + 1/3 = 2/3
  { a: 1, op: '+', b: 2, d: 4 }, // 1/4 + 2/4 = 3/4
  { a: 3, op: '-', b: 1, d: 4 }, // 3/4 − 1/4 = 2/4
  { a: 2, op: '-', b: 1, d: 3 }, // 2/3 − 1/3 = 1/3
  { a: 2, op: '+', b: 2, d: 4 }, // 2/4 + 2/4 = 4/4 (whole!)

  // ── Medium: sixths ──
  { a: 2, op: '+', b: 3, d: 6 }, // 2/6 + 3/6 = 5/6
  { a: 4, op: '-', b: 1, d: 6 }, // 4/6 − 1/6 = 3/6
  { a: 5, op: '-', b: 2, d: 6 }, // 5/6 − 2/6 = 3/6
  { a: 1, op: '+', b: 5, d: 6 }, // 1/6 + 5/6 = 6/6 (whole!)

  // ── Hard: eighths ──
  { a: 3, op: '+', b: 2, d: 8 }, // 3/8 + 2/8 = 5/8
  { a: 5, op: '-', b: 3, d: 8 }, // 5/8 − 3/8 = 2/8
  { a: 3, op: '+', b: 5, d: 8 }, // 3/8 + 5/8 = 8/8 (whole!)
  { a: 7, op: '-', b: 3, d: 8 }, // 7/8 − 3/8 = 4/8
  { a: 6, op: '-', b: 4, d: 8 }, // 6/8 − 4/8 = 2/8
];

function difficultyFor(s: Spec): DifficultyLevel {
  if (s.d >= 8) return DifficultyLevel.HARD;
  if (s.d >= 6) return DifficultyLevel.MEDIUM;
  return DifficultyLevel.EASY;
}

function toRound(s: Spec, idx: number): PizzaRound {
  const answer = s.op === '-' ? s.a - s.b : s.a + s.b;
  return {
    id: `pizza-${idx}-${s.a}${s.op}${s.b}-${s.d}`,
    den: s.d,
    op: s.op,
    aNum: s.a,
    bNum: s.b,
    answerNum: answer,
    difficulty: difficultyFor(s)
  };
}

/**
 * Build a session's rounds: sort into an easy→hard gradient (shuffled
 * within each difficulty band) and sample `count` evenly across it.
 */
export function buildCuttingPizzaRounds(count: number): PizzaRound[] {
  const rank: Record<DifficultyLevel, number> = {
    [DifficultyLevel.EASY]: 0,
    [DifficultyLevel.MEDIUM]: 1,
    [DifficultyLevel.HARD]: 2
  };
  const gradient = shuffleBands(POOL.map((s, i) => toRound(s, i)), r => rank[r.difficulty]);
  const n = gradient.length;
  const k = Math.min(count, n);
  if (k <= 0) return [];
  if (k === 1) return [gradient[0]];

  const used = new Set<number>();
  const out: PizzaRound[] = [];
  for (let i = 0; i < k; i++) {
    let idx = Math.round((i * (n - 1)) / (k - 1));
    while (used.has(idx)) idx = (idx + 1) % n;
    used.add(idx);
    out.push(gradient[idx]);
  }
  return out;
}

/** Group by a numeric key (ascending), shuffle within each band. */
function shuffleBands(rounds: PizzaRound[], keyFn: (r: PizzaRound) => number): PizzaRound[] {
  const bands = new Map<number, PizzaRound[]>();
  for (const r of rounds) {
    const key = keyFn(r);
    const band = bands.get(key) ?? [];
    band.push(r);
    bands.set(key, band);
  }
  const out: PizzaRound[] = [];
  [...bands.keys()].sort((a, b) => a - b).forEach(key => {
    const band = bands.get(key)!;
    for (let i = band.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [band[i], band[j]] = [band[j], band[i]];
    }
    out.push(...band);
  });
  return out;
}
